<script setup lang="ts">
import Error from 'assets/icons/error.svg'
import Search from 'assets/icons/search.svg'
import Close from 'assets/icons/close.svg'
import Trash from 'assets/icons/trash.svg'
import ArrowLong from 'assets/icons/arrow-long.svg'
import ImageUpload from 'assets/icons/upload-image.svg'
import { useDropZone } from '@vueuse/core'

const props = defineProps({
    modelValue: {
        type: [String, Array],
        default: () => [],
    },
    imageConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                startValidation: rawValue => rawValue?.startValidation === undefined || typeof rawValue?.startValidation === 'boolean',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Define static keys
const { t } = useI18n()

// Define error alert
const errorAlert = useState('error')
const successAlert = useState('success')

// Define heic2any (can only be imported client-side)
let heic2any = null

// Define the valid MIME types in an array
const validMimeTypes = [
    'image/webp', // Modern, efficient image format
    'image/jpeg', // Standard JPEG format
    'image/png', // PNG format with support for transparency
    'image/gif', // GIF format for animations
    'image/heic', // HEIC format for high-efficiency images
    'image/heif' // HEIF format for generic high-efficiency images
]

// Define some refs
const galleryDirection = ref('')
const isUploadPending = ref(false)
const imageUploadDiv = ref(null)
const showImageGallery = ref(false)
const selectedImageIndex = ref(0)
const dropZoneRef = ref<HTMLDivElement>()
const fileInput = ref<HTMLDivElement>()
const imageArray = ref(props.modelValue)
const imageArrayThumbnails = ref<string[]>([])

/**
 * Filter files by valid MIME types and size
 * @param {FileList} files
 * @returns {File[]} - Array of valid files
 */
const filterValidFiles = (files) => {
    return Array.from(files).filter(file => validMimeTypes.includes(file.type))
}

/**
 * Processes uploaded image files, filtering and limiting the number of valid files to 5.
 * Converts HEIC/HEIF/HEVC files to JPEG format, resizes and converts the images to base64 format,
 * and updates the application state with the processed images.
 *
 * @async
 * @function processUploadedImages
 * @param {Array<File>} files - An array of uploaded image files
 * @returns {Promise<void>} - A Promise that resolves when the processing is complete
 * @throws {Error} - If HEIC/HEIF/HEVC to JPEG conversion fails or any other processing step fails
 * @note Uses `filterValidFiles` function to filter and limit the number of valid files
 * @note Uses `heic2any` library to convert HEIC/HEIF/HEVC files to JPEG format
 * @note Uses `convertAndResizeFileToBase64` function to resize and convert images to base64 format
 * @note Updates `imageArrayThumbnails` and `imageArray` application state with the processed images
 * @note Displays success or error alerts based on the processing outcome
 */
const processUploadedImages = async (files) => {
    if (import.meta.client && !heic2any) {
        const heic2anyModule = await import('heic2any')
        heic2any = heic2anyModule.default || heic2anyModule
    }

    // Filter and limit the number of valid files to process (up to 5)
    const validFiles = filterValidFiles(files)?.slice(0, 5)

    if (validFiles.length === 0) {
        errorAlert.value = {
            type: 'image',
            label: `${t('form.form_image_upload_error')} ${t('form.form_image_upload_format_error')}`
        }
        return
    }

    try {
        isUploadPending.value = true
        errorAlert.value = null

        // Convert HEIC/HEIF/HEVC files to a supported format (e.g., JPEG)
        const convertedFiles = await Promise.all(validFiles.map(async (file) => {
            if (['image/heic', 'image/heif'].includes(file.type)) {
                try {
                    const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg' })
                    return new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' })
                } catch (error) {
                    console.log('HEIC/HEIF to JPEG conversion failed:', error)
                    // throw error

                    errorAlert.value = {
                        type: 'image',
                        label: `${t('form.form_image_upload_error')}`
                    }
                }
            }
            return file
        }))

        const convertedImages = await Promise.all(convertedFiles.map(file => convertAndResizeFileToBase64(file, 1400, 1400)))
        const convertedThumbnailImages = await Promise.all(convertedFiles.map(file => convertAndResizeFileToBase64(file, 150, 150)))

        imageArrayThumbnails.value = [...imageArrayThumbnails.value, ...convertedThumbnailImages]?.slice(0, 5)
        imageArray.value = [...imageArray.value, ...convertedImages]?.slice(0, 5)

        successAlert.value = {
            type: 'image',
            label: `${t('form.form_image_upload_success')}`
        }

        scrollToUpload()
    } catch (error) {
        console.log(error)

        errorAlert.value = {
            type: 'image',
            label: `${t('form.form_image_upload_error')}`
        }

        isUploadPending.value = false
    } finally {
        isUploadPending.value = false
    }
}

/**
 * Handle drop
 */
const onDrop = async () => {
    if (files.value) {
        await processUploadedImages(files.value)
    }
}

/**
 * Handle file change
 * @param event
 */
const handleFileChange = async (event) => {
    if (event.target.files) {
        await processUploadedImages(event.target.files)
    }
}

/**
 * Resize an image file to the desired dimensions.
 * @param {File} file - The image file to resize.
 * @param {number} maxWidth - The maximum width for the resized image.
 * @param {number} maxHeight - The maximum height for the resized image.
 * @returns {Promise<string>} - A promise that resolves to the resized image as a base64 string.
 */
const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        const reader = new FileReader()

        reader.onload = (e) => {
            img.src = e?.target?.result
        }

        reader.onerror = reject

        img.onload = () => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            let width = img.width
            let height = img.height

            if (width > height) {
                if (width > maxWidth) {
                    height *= maxWidth / width
                    width = maxWidth
                }
            } else {
                if (height > maxHeight) {
                    width *= maxHeight / height
                    height = maxHeight
                }
            }

            canvas.width = width
            canvas.height = height
            ctx?.drawImage(img, 0, 0, width, height)

            resolve(canvas.toDataURL('image/jpeg')) // You can change the format if needed
        }

        img.onerror = reject

        reader.readAsDataURL(file)
    })
}

/**
 * Convert file to base64 and resize it
 * @param {File} file
 * @param maxWidth
 * @param maxHeight
 * @returns {Promise<string>}
 */
const convertAndResizeFileToBase64 = async (file, maxWidth = 800, maxHeight = 800) => {
    return await resizeImage(file, maxWidth, maxHeight) // Adjust maxWidth and maxHeight as needed
}

/**
 * Delete image
 * @param index
 */
const deleteImage = (index: number) => {
    // Create a new array excluding the image at the specified index
    imageArray.value = imageArray.value.filter((_: string, i: number) => i !== index)
    imageArrayThumbnails.value = imageArrayThumbnails.value.filter((_: string, i: number) => i !== index)

    closeGallery()

    emit('update:modelValue', imageArray.value)
}

/**
 * Close Gallery
 */
const closeGallery = () => {
    showImageGallery.value = false
    scrollToUpload()
}

/**
 *  Scroll to upload
 */
const scrollToUpload = () => {
    const scrollElement = imageUploadDiv.value?.getBoundingClientRect()
    const distance = scrollElement.top + window.scrollY - 140

    // Scroll to target
    window.scrollTo({
        top: distance,
        behavior: 'smooth'
    })
}

/**
 * Show Gallery
 * @param index
 */
const initGallery = (index: number) => {
    showImageGallery.value = true
    selectedImageIndex.value = index
}

/**
 *  Trigger File Upload
 */
const triggerFileUpload = () => {
    fileInput.value?.click()
}

// Init dropzone
const { files, isOverDropZone } = useDropZone(dropZoneRef, {
    onDrop,
    // dataTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
})

/**
 * Handle Gallery
 * @param dir - 'prev' or 'next'
 */
const handleGalleryImage = (dir) => {
    // Get the current index and the total number of images
    const currentIndex = selectedImageIndex.value
    const totalImages = imageArray.value.length

    galleryDirection.value = dir

    // Check the direction and update the index accordingly
    if (dir === 'prev' && currentIndex > 0) {
        selectedImageIndex.value -= 1
    } else if (dir === 'next' && currentIndex < totalImages - 1) {
        selectedImageIndex.value += 1
    }
}

// Check if the image is uploaded
const imageIsUploaded = computed(() => imageArray.value.length > 0)

// Check max count uploaded images
const maxUploadedImages = computed(() => imageArray.value.length >= 5)

// Get dropzone class
const getDropZoneClass = computed(() => {
    return {
        'form-image-upload-active': isOverDropZone.value,
        'form-image-upload-error': errorAlert.value?.type === 'image',
    }
})

// Get gallery caption
const getGalleryCaption = computed(() => {
    return `Bild ${selectedImageIndex.value + 1} von ${imageArray.value?.length}`
})

// Get gallery class
const getGalleryClass = computed(() => {
    return {
        'fade-from-left': galleryDirection.value === 'prev',
        'fade-from-right': galleryDirection.value === 'next',
    }
})

// Define image error
const hasImageError = computed(() => (props.modelValue?.length < 1) && props.imageConfig?.startValidation)

// Define image label
const imageLabel = computed(() => hasImageError.value ? t('form.form_image_upload_mandatory_error') : t('form.form_image_upload_text'))

// Handle keydown events to navigate through gallery images and close gallery on Esc key
const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
        const focusableElements = getFocusableElements()
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
            // Shift+Tab - focus the previous focusable element
            if (document.activeElement === firstElement) {
                event.preventDefault()
                lastElement.focus() // Cycle to the last focusable element
            }
        } else {
            // Tab - focus the next focusable element
            if (document.activeElement === lastElement) {
                event.preventDefault()
                firstElement.focus() // Cycle to the first focusable element
            }
        }
    }

    // If Esc key is pressed, close the gallery
    if (event.key === 'Escape') {
        closeGallery()
    }
}

// Get focusable elements
const getFocusableElements = () => {
    return document.querySelectorAll(
        '.gallery-overlay-wrapper button, .gallery-overlay-wrapper a, .gallery-overlay-wrapper input, .gallery-overlay-wrapper select, .gallery-overlay-wrapper textarea, .gallery-overlay-wrapper [tabindex]:not([tabindex="-1"])'
    )
}

// Focus the first element when a gallery opens
const focusFirstElement = () => {
    const focusableElements = getFocusableElements()
    if (focusableElements.length > 0) {
        focusableElements[0].focus()
    }
}

// Define emits
const emit = defineEmits(['update:modelValue'])

// Emit updated value to parent component
watch(() => imageArray.value, (value) => {
    emit('update:modelValue', value)
})

// Watch for when the gallery overlay is shown to manage focus
watch(showImageGallery, (newValue) => {
    if (newValue) {
        focusFirstElement() // Focus the first element when gallery opens
    }
})
</script>

<template>
    <div
        ref="imageUploadDiv" class="form-image-upload-wrapper"
        :class="hasImageError ? 'form-error' : ''"
    >
        <div v-if="maxUploadedImages" class="form-image-upload-max-hint" role="alert">
            <p class="form-image-upload-text">
                <ImageUpload />
                <span>{{ t('form.form_image_upload_max_text') }}</span>
            </p>
        </div>

        <div
            v-else
            :class="getDropZoneClass"
            class="form-image-upload-container"
            :aria-label="t('form.form_image_upload_ariaLabel')"
            role="button"
            tabindex="1"
            @keydown.space.prevent="triggerFileUpload"
            @keydown.enter.prevent="triggerFileUpload"
            @click="triggerFileUpload"
        >
            <LoadingSpinner v-if="isUploadPending" class="container-loader" aria-live="polite" />

            <template v-else>
                <input
                    ref="fileInput"
                    class="form-image-upload-input-file"
                    type="file"
                    accept="image/*"
                    multiple
                    :aria-label="imageLabel"
                    @change="handleFileChange"
                >

                <div ref="dropZoneRef" class="form-image-upload-dropzone" :aria-label="t('form.form_image_upload_ariaLabel')">
                    <p class="form-image-upload-text">
                        <ImageUpload />
                        <span v-html="imageLabel" />
                    </p>
                </div>
            </template>
        </div>

        <div class="form-image-upload-thumbnails-container">
            <div v-for="index in 5" :key="index" class="form-image-upload-thumbnails-item" />

            <ul v-if="imageIsUploaded" class="form-image-upload-thumbnails-list" :aria-label="t('form.form_image_upload_imageList')">
                <li v-for="(src, index) in imageArrayThumbnails" :key="src + index">
                    <div class="uploaded-image-container">
                        <img
                            class="uploaded-image"
                            :src="src"
                            alt="Uploaded Image"
                            :aria-label="t('form.form_image_view_image') + (index + 1)"
                            @click="initGallery(index)"
                        >

                        <button
                            class="image-upload-action-detail"
                            :aria-label="t('form.form_image_view_details')"
                            @click="initGallery(index)"
                        >
                            <Search />
                        </button>
                    </div>

                    <button
                        class="image-upload-action-delete"
                        :title="t('form.form_image_upload_action_delete')"
                        :aria-label="t('form.form_image_delete_image')"
                        @click="deleteImage(index)"
                    >
                        <Error />
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <Transition name="fade" mode="out-in">
        <div
            v-if="showImageGallery"
            :aria-hidden="showImageGallery"
            class="gallery-overlay-wrapper"
            aria-live="polite"
            role="dialog"
            @keydown="handleKeyDown"
        >
            <div class="gallery-overlay">
                <img
                    :key="selectedImageIndex"
                    class="gallery-image"
                    :class="getGalleryClass"
                    :src="imageArray[selectedImageIndex]"
                    :alt="getGalleryCaption"
                    :aria-label="getGalleryCaption"
                >
                <button
                    v-if="imageArray.length > 1"
                    class="gallery-prev"
                    :class="{ 'gallery-controls-inactive': selectedImageIndex === 0 }"
                    :aria-label="t('form.form_image_gallery_prev')"
                    @pointerdown="handleGalleryImage('prev')"
                >
                    <ArrowLong />
                </button>

                <button
                    v-if="imageArray.length > 1"
                    class="gallery-next"
                    :class="{ 'gallery-controls-inactive': selectedImageIndex === imageArray.length - 1 }"
                    :aria-label="t('form.form_image_gallery_next')"
                    @pointerdown="handleGalleryImage('next')"
                >
                    <ArrowLong />
                </button>
            </div>

            <button class="close-gallery-overlay" :aria-label="t('form.form_image_close_gallery')" @click="closeGallery">
                <Close />
            </button>

            <div class="gallery-action-bar">
                {{ getGalleryCaption }}
                <button
                    class="delete-gallery-image"
                    :aria-label="t('form_image_delete_image')"
                    @click="deleteImage(selectedImageIndex)"
                >
                    <Trash /> {{ t('form.form_image_upload_action_delete') }}
                </button>
            </div>
        </div>
    </Transition>
</template>

<style lang="scss">
.form-image-upload-wrapper {
    display: grid;
    grid-template-rows:  1fr auto;
    padding-block-end: 20px;
    position: relative;
}

.form-image-upload-container,
.form-image-upload-max-hint {
    border-radius: 10px;
    background: $form-input-text-bg;
    color: $form-input-text;
    position: relative;
    margin-block-start: px2rem(-40);
    height: 400px;

    @include bp(medium1) {
        margin-block-start: px2rem(30);
        height: auto;
    }

    .form-error & {
        color: $form-error;
    }
}

.form-image-upload-container {
    border: $form-input-text-border 2px dashed;

    .form-error & {
        border-color: $form-error;
    }

    &.form-image-upload-active {
        background: $dropzone-active-bg;
        border-color: $dropzone-active-border;
    }

    &.form-image-upload-error {
        color: $dropzone-error;
        background: $dropzone-error-bg;
        border-color: $dropzone-error-border;
    }
}

.form-image-upload-max-hint {
    display: flex;
    align-items: center;
    justify-content: center;
}

.form-image-upload-dropzone {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 400ms ease;

    &:hover {
        background: $dropzone-hover-bg;
    }
}

.form-image-upload-input-file {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
}

.form-image-upload-text {
    max-width: 70%;
    text-align: center;
    font-size: px2rem(14);

    .form-image-upload-max-hint & {
        opacity: 0.5;
    }

    svg {
        fill: currentcolor;
        width: 50px;
        display: block;
        margin: 20px auto;

        @include bp(large1) {
            width: 100px;
        }
    }
}

.form-image-upload-thumbnails-container {
    margin-block-start: px2rem(30);
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 15px;
    position: relative;

    .form-image-upload-thumbnails-item {
        position: relative;
        height: 100%;
        display: flex;
        align-items: flex-end;
        border-radius: 10px;
        background: $uploaded-image-container-bg;
        overflow: hidden;
        aspect-ratio:  1 / 1;
        width: 100%;
    }
}

.form-image-upload-thumbnails-list {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 15px;
    padding: 0;
    margin: 0;
    list-style: none;
    width: 100%;
    height: 100%;

    li {
        position: relative;
        height: 100%;
        display: flex;
        align-items: flex-end;
    }
}

.uploaded-image-container {
    border-radius: 10px;
    overflow: hidden;
    aspect-ratio:  1 / 1;
    position: relative;
    width: 100%;

    @media (hover: hover) {
        &:hover  {
            .image-upload-action-detail {
                opacity: 1;
                pointer-events: all;
                cursor: pointer;
            }
        }
    }
}

.uploaded-image {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
}

.image-upload-action-detail {
    position: absolute;
    inset: 0;
    background: $image-upload-action-detail-hover-bg;
    opacity: 0;
    transition: opacity 400ms ease;
    z-index: z('image-upload-action-detail');
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    border: 0 none;

    svg {
        width: 20px;
        height: 20px;
        fill: $image-upload-action-detail-fill;
    }
}

.image-upload-action-delete {
    cursor: pointer;
    position: absolute;
    z-index: z('image-upload-action-delete');
    bottom: -10px;
    right: -10px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    background: none;
    border: 0 none;

    svg {
        width: 20px;
        height: 20px;
        fill: $image-upload-action-delete;
    }
}

.gallery-overlay-wrapper {
    position: fixed;
    inset: 0;
    z-index: z('gallery-overlay');
    background: $gallery-overlay-bg;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-gallery-overlay {
    width: 60px;
    height: 60px;
    background: $close-gallery-overlay-bg;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
    appearance: none;
    border: 0 none;

    svg {
        width: 20px;
        height: 20px;
        fill: $close-gallery-overlay;
    }
}

.gallery-overlay {
    margin: auto;
    padding-block: px2rem(60) px2rem(120)
}

.gallery-image {
    max-width: calc(100% - 80px);
    max-height: 80vh;
    width: auto;
    height: auto;
    display: block;
    margin: auto;

    &.fade-from-left {
        animation:  fade-from-left 0.5s ease-in-out forwards;
    }

    &.fade-from-right {
        animation:  fade-from-right 0.5s ease-in-out forwards;
    }

}

.gallery-action-bar {
    position: absolute;
    bottom: 0;
    width: 100%;
    background: $gallery-action-bar-bg;
    padding: 0 px2rem(20) 0 px2rem(70);
    display: flex;
    height: 60px;
    justify-content: space-between;
    align-items: center;
    border-top: $gallery-action-bar-border 2px solid;
}

.delete-gallery-image {
    white-space: nowrap;
    z-index: 1;
    display: flex;
    align-items: center;
    cursor: pointer;
    appearance: none;
    border: 0 none;
    background: none;
    font-size: inherit;
    color: inherit;
    font-family: inherit;

    svg {
        width: 20px;
        height: 20px;
        fill: $close-gallery-overlay;
        margin-inline-end: 10px;
    }
}

.gallery-prev,
.gallery-next {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    height: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    appearance: none;
    background: none;
    border: 0 none;
    color: inherit;

    &.gallery-controls-inactive {
        opacity: 0.1;
        pointer-events: none;
        background: transparent;
    }

    svg {
        width: 30px;
        height: 30px;
        fill: currentcolor;
        margin-inline: px2rem(50);
        margin-block-start: px2rem(-50);

        @include bp(medium1) {
            width: 50px;
            height: 50px;
        }
    }
}

.gallery-prev {
    left: 0;
    background: $gallery-prev-gradient;
    justify-content: flex-start;

    svg {
        transform: rotate(180deg);
    }
}

.gallery-next {
    justify-content: flex-end;
    right: 0;
    background: $gallery-next-gradient;
}

@keyframes fade-from-right {
    from {
        transform: translateX(10%);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}

@keyframes fade-from-left {
    from {
        transform: translateX(-10%);
        opacity: 0.6;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>
